import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MsalProvider } from "@azure/msal-react";
import { EventType, type AuthenticationResult } from "@azure/msal-browser";
import App from "./App";
import { appConfig } from "@/config/appConfig";
import { pca } from "@/auth/msalConfig";
import { AuthGate } from "@/auth/AuthGate";
import { TokenProviderWiring } from "@/auth/TokenProviderWiring";
import { BrandedLoader } from "@/components/BrandedLoader";
import "./styles/colors_and_type.css";
import "./styles/styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      gcTime: 10 * 60_000,
      retry: (failureCount, error) => {
        const status = (error as { status?: number })?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: true,
    },
  },
});

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error("Missing #root element");
}
const root = ReactDOM.createRoot(rootEl);

const innerTree = (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </QueryClientProvider>
);

async function bootstrap() {
  if (appConfig.msal.disabled) {
    root.render(<React.StrictMode>{innerTree}</React.StrictMode>);
    return;
  }

  // Paint a branded loader immediately so the MSAL handshake below (initialize +
  // handleRedirectPromise, plus the AAD round-trip) doesn't leave a blank screen.
  root.render(<BrandedLoader variant="fullscreen" label="Loading…" />);

  // MSAL v3 requires explicit initialize() before any other API call. handleRedirectPromise()
  // consumes the auth response after AAD redirects back to us — without it the user lands on a
  // hash-fragment URL with no active account.
  await pca.initialize();
  const redirectResult = await pca.handleRedirectPromise();
  if (redirectResult?.account) {
    pca.setActiveAccount(redirectResult.account);
  } else if (!pca.getActiveAccount()) {
    const accounts = pca.getAllAccounts();
    if (accounts[0]) {
      pca.setActiveAccount(accounts[0]);
    }
  }

  pca.addEventCallback((event) => {
    if (
      (event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
      (event.payload as AuthenticationResult)?.account
    ) {
      pca.setActiveAccount((event.payload as AuthenticationResult).account);
    }
  });

  root.render(
    <React.StrictMode>
      <MsalProvider instance={pca}>
        <TokenProviderWiring />
        <AuthGate>{innerTree}</AuthGate>
      </MsalProvider>
    </React.StrictMode>,
  );
}

bootstrap().catch((err) => {
  console.error("Bootstrap failed", err);
  rootEl!.innerHTML =
    '<main class="screen"><h1>App failed to start</h1><p>Open the console for details.</p></main>';
});
